import { PropTypes } from 'prop-types';
import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress } from '@material-ui/core';
import JsonView from '@uiw/react-json-view';
import Utils from '@/common/utils/utils';
import Constant, { JSON_VIEW_STYLE } from '@/common/constant/Constant';
import FormInputWithOnChange from '@/component/common/form/input-with-onchange';
import { readJsonFromUrl } from '../handler/JsonFieldHandler';
import Logger from '@/common/utils/Logger';

export default function JsonFieldUI(props) {
    const defaultJsonUrl = props?.defaultValue || '';
    const [jsonEditValue, setJsonEditValue] = useState('');
    const [isLoadingJson, setIsLoadingJson] = useState(false);
    const [isPreviewMode, setIsPreviewMode] = useState(true);
    const [errMsg, setErrMsg] = useState('');

    const publicForCustomer = props.data.structure.publicForCustomer;
    const isAddNewTicket = !props?.detailTicket;
    const isViewOnly =
        Number(publicForCustomer) === Constant.SETTING_FORM_DEFAULT.READ_ONLY ||
        props.viewFromProjectOfEscalateTicket ||
        !Utils.isEmptyString(props.detailTicket?.deletedAt) ||
        props.hasEditFieldPermissions === false;

    // Sync error message from both react-hook-form errorlist and field.error (set by validateExceedSizeOfFieldTicket)
    useEffect(() => {
        let newMsg = '';
        if (props.errorlist && props.errorlist.hasOwnProperty(props?.name)) {
            newMsg = props.errorlist[props?.name].message;
        }
        if (!Utils.isEmptyObject(props.data?.error)) {
            newMsg = props.data?.error?.message;
        }
        setErrMsg(newMsg);

        const isDetailTicket = !Utils.isEmptyObject(props?.detailTicket);
        if (!Utils.isValidUrl(defaultJsonUrl) && isDetailTicket) {
            props?.setValue(props?.name, defaultJsonUrl || '');
            return setJsonEditValue(defaultJsonUrl || '');
        }
    }, [props.errorlist, props.data?.error]);

    const handleChangeJson = e => {
        if (isViewOnly) return;
        if (props?.setIsEdited) {
            props?.setIsEdited(true);
            props?.setShowButtonEditTicket && props?.setShowButtonEditTicket();
            if (!props?.listFieldEdited?.includes(props?.name)) {
                props?.setListFieldEdited([...props.listFieldEdited, props?.name]);
            }
            if (props?.setOnChangeEdit) {
                props?.setOnChangeEdit(!props.onChangeEdit);
            }
        }
        setJsonEditValue(e.target.value);
    };

    /** Apply: store current textarea content and switch to View Mode */
    const handleApplyJson = () => {
        if (isViewOnly) return;
        props?.setValue(props?.name, jsonEditValue);
        setIsPreviewMode(true);
    };

    /** Cancel: restore original content and switch to View Mode */
    const handleCancelJson = async () => {
        if (isViewOnly) return;
        props?.clearErrors(props?.name);
        setErrMsg('');

        setIsLoadingJson(true);

        const oldJson = await readJsonFromUrl(defaultJsonUrl);
        props?.setValue(props?.name, oldJson);
        setJsonEditValue(oldJson);
        setIsPreviewMode(true);

        setIsLoadingJson(false);
    };

    /** Render View Mode content: JSON tree for valid JSON, <pre> for invalid */
    const renderViewContent = () => {
        const content = jsonEditValue;
        if (!content) {
            return <div className="json-field-empty-view" aria-label="empty json field" />;
        }
        try {
            const parsed = JSON.parse(content);
            return (
                <JsonView
                    value={parsed || ''}
                    collapsed={2}
                    enableClipboard={false}
                    displayDataTypes={false}
                    style={JSON_VIEW_STYLE}
                />
            );
        } catch {
            return <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: 0 }}>{content}</pre>;
        }
    };

    // Switch to Edit Mode on new ticket, View Mode on existing ticket
    useEffect(() => {
        setIsPreviewMode(!isAddNewTicket);
    }, [props?.detailTicket]);

    // Load content from S3 on init / selectedForm change
    useEffect(() => {
        let isKnowledgeForm = [Constant.FORM_TYPE.FORM_KNOWLEDGE, Constant.FORM_TYPE.FORM_MASTER].includes(props?.parentForm?.type);
        if (isAddNewTicket || props?.isDraftMode || (props?.editTicket && !isKnowledgeForm)) {
            props.register(props?.name, {
                onChange: e => {}
            });

            setIsLoadingJson(true);
            readJsonFromUrl(defaultJsonUrl)
                .then(jsonText => {
                    props?.setValue(props?.name, jsonText || '');
                    setJsonEditValue(jsonText || '');
                })
                .catch(err => Logger.error('JsonFieldUI load err:', err))
                .finally(() => { setIsLoadingJson(false); });
        }
    }, [props?.parentForm]);

    // Reload when flagReset or displayMode changes to VIEW
    useEffect(() => {
        if (props?.flagReset || props?.displayMode === Constant.DISPLAY_MODE.VIEW) {
            props?.clearErrors(props?.name);
            setErrMsg('');

            setIsLoadingJson(true);
            readJsonFromUrl(defaultJsonUrl)
                .then(jsonText => {
                    props?.setValue(props?.name, jsonText || '');
                    setJsonEditValue(jsonText || '');
                    setIsPreviewMode(true);
                    props.setFlagReset && props.setFlagReset(false);
                })
                .catch(err => Logger.error('JsonFieldUI flagReset err:', err))
                .finally(() => { setIsLoadingJson(false); });
        }
    }, [props?.flagReset, props?.displayMode, props?.defaultValue]);

    return (
        <div className="form-addTicket field-json" style={{ position: 'relative' }}>
            <div className="cell-prod-status gap-8">
                <label
                    className={`w-f100 ${publicForCustomer ? 'form-addTicket-label-color' : 'form-addTicket-label'}`}
                >
                    {props?.label}{' '}
                    {props.data.structure.required && <span className="required">*</span>}
                </label>
            </div>

            <div className="form-addTicket-input">
                {/* View Mode */}
                <Box style={{ display: isPreviewMode ? 'block' : 'none' }}>
                    <Box display="inline-flex" sx={{ width: '100%' }}>
                        <Box
                            style={{
                                flex: 1,
                                border: errMsg ? '1px solid #f44336' : '1px solid #ccc',
                                borderRadius: '4px',
                                padding: '8px',
                                minHeight: '150px',
                                height:"auto",
                                overflow: 'auto',
                                backgroundColor: '#f2f3f5'
                            }}
                        >
                            {renderViewContent()}
                        </Box>

                        {!isViewOnly && (
                            <div>
                                <Button
                                    className="button-mermaid cursor-default"
                                    style={{ paddingRight: '0px' }}
                                >
                                    <img
                                        src="/assets/image/button-url-edit.svg"
                                        alt=""
                                        className="cursor-pointer"
                                        onClick={() => setIsPreviewMode(false)}
                                    />
                                </Button>
                            </div>
                        )}
                    </Box>
                </Box>

                {/* Edit Mode */}
                <Box style={{ display: !isPreviewMode ? 'block' : 'none' }}>
                    <Box display="flex" sx={{ width: '100%', position: 'relative' }}>
                        <Box sx={{ flexGrow: 1, paddingRight: '10px' }}>
                            <FormInputWithOnChange
                                name={props?.name}
                                className="form-control form-control-wrapTextarea form-control-hg200"
                                variant="outlined"
                                defaultValue={jsonEditValue}
                                value={jsonEditValue}
                                multiline
                                disabled={isViewOnly}
                                errorlist={errMsg
                                    ? { ...props?.errorlist, [props?.name]: { message: errMsg } }
                                    : props?.errorlist
                                }
                                autoComplete="off"
                                required={props?.required}
                                onChange={handleChangeJson}
                                data-cy="json-field-textarea"
                                isDisableOnBlur={props.isDisableOnBlur ? props.isDisableOnBlur : true}
                            />
                        </Box>
                        <Box display="inline-flex" alignItems="flex-start">
                            <Button
                                className="button-mermaid cursor-default"
                                onClick={handleApplyJson}
                            >
                                <img src="/assets/image/button-url-apply.svg" alt="" className="cursor-pointer" />
                            </Button>
                            <Button
                                className="button-mermaid cursor-default"
                                onClick={handleCancelJson}
                                style={{ paddingRight: '0px' }}
                            >
                                <img src="/assets/image/button-url-reset.svg" alt="" className="cursor-pointer" />
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </div>

            {/* Error message — shown in view mode since FormInputWithOnChange handles it in edit mode */}
            {errMsg && isPreviewMode && (
                <p className="MuiFormHelperText-root MuiFormHelperText-contained Mui-error" style={{ color: '#f44336' }}>{errMsg}</p>
            )}

            {isLoadingJson && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: 'rgba(255,255,255,0.7)',
                        zIndex: 1
                    }}
                >
                    <CircularProgress />
                </Box>
            )}
        </div>
    );
}

JsonFieldUI.propTypes = {
    name: PropTypes.string,
    label: PropTypes.string,
    defaultValue: PropTypes.string,
    data: PropTypes.any,
    detailTicket: PropTypes.any,
    parentForm: PropTypes.any,
    errorlist: PropTypes.any,
    flagReset: PropTypes.any,
    displayMode: PropTypes.any,
    tabId: PropTypes.any,
    hasEditFieldPermissions: PropTypes.any,
    viewFromProjectOfEscalateTicket: PropTypes.any,
    isDraftMode: PropTypes.any,
    editTicket: PropTypes.any,
    required: PropTypes.any,
    isDisableOnBlur: PropTypes.any,
    setIsEdited: PropTypes.any,
    setShowButtonEditTicket: PropTypes.any,
    setListFieldEdited: PropTypes.any,
    setOnChangeEdit: PropTypes.any,
    setFlagReset: PropTypes.any,
    listFieldEdited: PropTypes.any,
    onChangeEdit: PropTypes.any,
    setValue: PropTypes.any,
    clearErrors: PropTypes.any,
    register: PropTypes.any
};
